/**
 * FollowUpsPanel — body content for the "Follow-ups" tab on both /lab/bookings and
 * /patient/bookings. Fetches booking-request records (created by "Book Now" clicks
 * from Smart Reports) and renders them with role-appropriate actions.
 *
 * Props:
 *   role: 'lab_admin' | 'patient'
 */
import React, { useCallback, useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, Phone, Mail, MapPin, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";

const LAB_STATUSES = [
  { id: "", label: "All" },
  { id: "new", label: "New" },
  { id: "contacted", label: "Contacted" },
  { id: "confirmed", label: "Confirmed" },
  { id: "completed", label: "Completed" },
  { id: "cancelled", label: "Cancelled" },
];

export default function FollowUpsPanel({ role }) {
  const [status, setStatus] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const endpoint = role === "patient" ? "/patient/booking-requests" : "/lab/booking-requests";

  const load = useCallback(() => {
    setLoading(true);
    const q = status ? `?status=${status}` : "";
    api.get(`${endpoint}${q}`)
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(apiError(e, "Could not load follow-ups")))
      .finally(() => setLoading(false));
  }, [endpoint, status]);

  useEffect(() => { load(); }, [load]);

  const advance = async (id, next) => {
    try {
      await api.patch(`/lab/booking-requests/${id}`, { status: next });
      toast.success(`Marked ${next}`);
      load();
    } catch (e) {
      toast.error(apiError(e, "Update failed"));
    }
  };

  return (
    <div className="space-y-4" data-testid="followups-panel">
      {role !== "patient" && (
        <div className="flex gap-2 flex-wrap" data-testid="fu-filters">
          {LAB_STATUSES.map((s) => (
            <button
              key={s.id}
              onClick={() => setStatus(s.id)}
              data-testid={`fu-filter-${s.id || "all"}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border ${status === s.id ? "bg-ink text-white border-ink" : "bg-white text-ink/70 border-border hover:border-ink/30"}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {loading && <Card className="p-10 rounded-2xl text-center text-ink/50">Loading follow-ups…</Card>}
      {!loading && rows.length === 0 && (
        <Card className="p-10 rounded-2xl text-center text-ink/60" data-testid="fu-empty">
          {role === "patient"
            ? "No follow-up requests yet. Tap “Book Now” on any Smart Report to request follow-up tests."
            : "No follow-up requests in this view."}
        </Card>
      )}

      {rows.map((b) => (
        <Card key={b.id} className="p-5 rounded-2xl" data-testid={`fu-card-${b.id}`}>
          <div className="grid md:grid-cols-[1fr_auto] gap-4">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-heading font-semibold text-ink text-lg">{b.patient_name}</div>
                <span className={`pill text-[10px] ${b.status === "new" ? "pill-borderline" : b.status === "cancelled" ? "pill-abnormal" : "pill-normal"}`}>{b.status}</span>
                <span className="text-xs text-ink/40">{new Date(b.created_at).toLocaleString()}</span>
              </div>
              <div className="mt-2 grid sm:grid-cols-2 gap-y-1 gap-x-4 text-sm">
                <span className="flex items-center gap-1.5 text-ink/70"><Phone className="w-3.5 h-3.5" /> {b.patient_phone}</span>
                {b.patient_email && <span className="flex items-center gap-1.5 text-ink/70"><Mail className="w-3.5 h-3.5" /> {b.patient_email}</span>}
                <span className="flex items-center gap-1.5 text-ink/70 col-span-full"><MapPin className="w-3.5 h-3.5" /> {b.collection_type === "home" ? (b.address || "Home collection") : "Lab visit"}</span>
                {b.report_id && (
                  <span className="flex items-center gap-1.5 text-ink/50 text-xs col-span-full">
                    <FileText className="w-3.5 h-3.5" /> Source: Smart Report · {b.report_id.slice(0, 8).toUpperCase()}
                  </span>
                )}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {(b.tests || []).map((t) => (
                  <span key={t} className="px-2 py-0.5 rounded-full text-xs bg-efgh-green/10 text-efgh-green border border-efgh-green/20">{t}</span>
                ))}
              </div>
              {b.preferred_date && (
                <div className="mt-2 text-xs text-ink/60">Preferred: {b.preferred_date} {b.preferred_time || ""}</div>
              )}
              {b.notes && <div className="mt-2 text-xs text-ink/70">Notes: {b.notes}</div>}
              {b.status_note && role === "patient" && (
                <div className="mt-2 text-xs text-ink/60"><b>Lab note:</b> {b.status_note}</div>
              )}
            </div>
            {role !== "patient" && (
              <div className="flex flex-col gap-2 items-stretch min-w-[140px]">
                {b.status === "new" && (
                  <Button size="sm" onClick={() => advance(b.id, "contacted")} className="bg-ink hover:bg-ink/90 text-white" data-testid={`fu-mark-contacted-${b.id}`}>Mark contacted</Button>
                )}
                {b.status === "contacted" && (
                  <Button size="sm" onClick={() => advance(b.id, "confirmed")} className="bg-efgh-green hover:bg-efgh-green/90 text-white" data-testid={`fu-mark-confirmed-${b.id}`}>Mark confirmed</Button>
                )}
                {b.status === "confirmed" && (
                  <Button size="sm" onClick={() => advance(b.id, "completed")} className="bg-efgh-green hover:bg-efgh-green/90 text-white" data-testid={`fu-mark-completed-${b.id}`}>Mark completed</Button>
                )}
                {["new", "contacted", "confirmed"].includes(b.status) && (
                  <Button size="sm" variant="outline" onClick={() => advance(b.id, "cancelled")} data-testid={`fu-cancel-${b.id}`}>Cancel</Button>
                )}
              </div>
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}
